import { ReactNode } from 'react';
import { DialogProps } from '@mui/material/Dialog';
import { Breakpoint } from '@mui/system';

import { SyntheticEventWithMetaKey } from '/common/models';

export enum ConfirmDialogType {
  Confirm = 'Confirm',
  Error = 'Error',
  Warning = 'Warning',
  Delete = 'Delete',
  SoftDelete = 'SoftDelete',
  Edit = 'Edit',
}

export type IConfirmDialogColors = 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';

export type IConfirmButtonBase = {
  label?: string;
  color?: IConfirmDialogColors;
};

export type IConfirmDialogColorResult = {
  name: IConfirmDialogColors;
  hex: string;
};

/**
 * Structure for split-text descriptions to allow styling the entity name in the middle.
 *
 * @remarks
 * **Visual Layout:**
 * - **Line 1:** `part1` **[Entity Name]** `part2`
 * - **Line 2:** `part3`
 *
 * @example
 * // Renders:
 * // "Edit promoted [Item Name]?"
 * // "This will create a new version."
 * {
 * part1: <span>Edit <b>promoted</b></span>, // ReactNode allowed
 * part2: "?",
 * part3: "This will create a new version."
 * }
 */
export type IConfirmDialogDescription = { part1?: ReactNode; part2?: ReactNode; part3?: ReactNode };

/**
 * Configuration props for the ConfirmDialog.
 *
 * @remarks
 * **Highly Recommended Usage:**
 * Instead of manually passing `title` and `content`, prefer using the semantic combination of:
 * 1. `type` (Defines the intent: Delete, Edit, Warning)
 * 2. `entityType` (What kind of object: 'Product', 'User')
 * 3. `entityName` (The specific item: 'iPhone 15', 'John Doe')
 * 4. `description` (Optional: Custom split text if defaults don't fit)
 *
 * This ensures consistent Titles, Colors, Button Labels, and Entity Highlighting across the app.
 */
export interface IConfirmProps {
  /**
   * The mode of the dialog. Controls default colors, icons, and text generation.
   * Defaults to `ConfirmDialogType.Confirm`.
   */
  type?: ConfirmDialogType;

  /**
   * The class or category of the item (e.g., 'Game event', 'Experiment', 'File').
   * Used to generate titles like "Edit promoted {Experiment}?".
   */
  entityType?: string;

  /**
   * The specific name of the item (e.g., 'Winter game event').
   * This text will be highlighted/styled within the description.
   */
  entityName?: string;

  /**
   * Overrides the generated title.
   * *Prefer using `type` + `entityType` generation when possible.*
   */
  title?: string;

  /**
   * Fully custom content.
   * If provided, it overrides the auto-generated split description.
   */
  content?: ReactNode | ((closeButton: () => void) => ReactNode);

  /**
   * Custom split description.
   * Use this if you need custom text but still want the standard `part1 [Entity] part2` layout.
   */
  description?: IConfirmDialogDescription;

  /** Configuration for the Cancel/Close button. Pass `null` to hide. */
  closeButton?:
    | (IConfirmButtonBase & {
        onClick?: (cb: () => void) => void;
      })
    | null;

  /** Configuration for the Confirm/Success button. Pass `null` to hide. */
  successButton?:
    | (IConfirmButtonBase & {
        onClick?: (event?: SyntheticEventWithMetaKey) => void;
      })
    | null;

  /**
   * **Action Callback**
   * Triggered **ONLY** when the dialog is closed via:
   * 1. Clicking the "Cancel" button.
   * 2. Clicking the backdrop/mask.
   * 3. Pressing Escape.
   * * *Does not fire on Success.*
   */
  onClose?: () => void;

  /**
   * **Action Callback**
   * Triggered **ONLY** when the dialog is confirmed via the "Success/Confirm" button.
   */
  onSuccess?: () => void;

  /**
   * **Lifecycle Callback**
   * Triggered **ALWAYS** when the dialog closes, regardless of the reason.
   * * Fires after `onClose` (cancellation) OR `onSuccess` (confirmation).
   * Use this for global cleanup, state resets, or refreshing parent data.
   */
  afterClose?: () => void;

  maxWidth?: DialogProps['maxWidth'];
  TransitionComponent?: DialogProps['TransitionComponent'];
}

export interface IConfirmDialogProps extends IConfirmProps {
  open: boolean;
}

export interface IGeneratedDefaults {
  title: string;
  description: IConfirmDialogDescription;
  confirmLabel: string;
  cancelLabel: string;
  entityName: string;
}

export type IConfirmDialogHeaderProps = Pick<IConfirmProps, 'title' | 'type' | 'maxWidth'> & {
  maxWidth: Breakpoint;
  mainColor: IConfirmDialogColorResult;
};

export type IConfirmDialogActionsProps = Pick<
  IConfirmProps,
  'closeButton' | 'successButton' | 'onClose' | 'onSuccess'
> & {
  mainColor: IConfirmDialogColorResult;
  confirmLabelDefault: string;
  cancelLabelDefault: string;
};

export type IConfirmDialogContentProps = {
  mainColor: IConfirmDialogColorResult;
  dialogContent?: ReactNode;
  typedContent?: IGeneratedDefaults;
};
